/**
 * O cliente da API do orçamento da Manutenção (`/api/v1/manutencao/orcamentos`, mig 782). Os tipos espelham as respostas
 * de `server/lib/manutencao/orcamento/orcamentos.ts`. Erro de regra chega com a FRASE em `mensagem` (`mensagemDoErro`).
 */
import { api } from '@/lib/api';

export { mensagemDoErro } from '@/pages/os-sistema/os-api';

export type StatusDoOrcamento = 'novo' | 'enviado' | 'aprovado' | 'local' | 'contrato' | 'compras' | 'recusado';
export type AbaDaFila = 'montar' | 'andamento' | 'aprovados' | 'compras' | 'recusados';

/** As abas da fila e o que cada uma junta — a mesma lista do servidor (`ABAS_DA_FILA`). */
export const ABAS: Array<{ id: AbaDaFila; nome: string; status: StatusDoOrcamento[] }> = [
  { id: 'montar', nome: 'A montar', status: ['novo'] },
  { id: 'andamento', nome: 'Em andamento', status: ['enviado'] },
  { id: 'aprovados', nome: 'Aprovados', status: ['aprovado', 'local', 'contrato'] },
  { id: 'compras', nome: 'No Compras', status: ['compras'] },
  { id: 'recusados', nome: 'Recusados', status: ['recusado'] },
];

export const ROTULO_DO_STATUS: Record<StatusDoOrcamento, string> = {
  novo: 'A montar',
  enviado: 'Em andamento',
  aprovado: 'Aprovado',
  local: 'Aprovado no local',
  contrato: 'Aprovado pelo contrato',
  compras: 'No Compras',
  recusado: 'Recusado',
};

export interface LinhaDaFila {
  id: string;
  codigo: string;
  clienteNome: string;
  titulo: string;
  status: StatusDoOrcamento;
  origem: 'escritorio' | 'tecnico';
  totalCentavos: number;
  enviadoVezes: number;
  enviadoEmIso: string | null;
  respondidoEmIso: string | null;
  osVenda: string | null;
  criadoEmIso: string;
}

export interface ItemDoOrcamento {
  id: string;
  codigoProduto: string | null;
  descricao: string;
  unidade: string | null;
  quantidade: number;
  custoUnitCentavos: number;
  markupPct: number;
  precoUnitCentavos: number;
  totalCentavos: number;
}

export interface Conta {
  materialCentavos: number;
  maoCentavos: number;
  subtotalCentavos: number;
  descontoCentavos: number;
  totalCentavos: number;
}

export interface DetalheDoOrcamento {
  id: string;
  numero: number;
  codigo: string;
  clienteId: string;
  clienteNome: string;
  osId: string | null;
  osNumero: number | null;
  empresaId: string;
  origem: 'escritorio' | 'tecnico';
  autorNome: string | null;
  pergunta: 'material' | 'comprar' | null;
  titulo: string;
  motivo: string;
  status: StatusDoOrcamento;
  versao: number;
  versaoEnviada: number | null;
  mudouDepoisDeEnviar: boolean;
  mao: { modo: 'diaria' | 'hora' | null; quantidade: number; baseCentavos: number; markupPct: number };
  desconto: { modo: 'pct' | 'valor' | null; pct: number; centavos: number };
  pagamento: string | null;
  prazoEntrega: string | null;
  prazoRetornoDias: number;
  itens: ItemDoOrcamento[];
  conta: Conta;
  pagamentosQueValem: string[];
  destinos: Array<{ email: string; podeAprovar: boolean }>;
  envio: { vezes: number; ultimoEmIso: string | null; porNome: string | null };
  resposta: { emIso: string | null; porEmail: string | null; nome: string | null; funcao: string | null; porUsuario: string | null; motivoDaRecusa: string | null };
  osVenda: string | null;
  versoes: Array<{ versao: number; mudou: string | null; porNome: string | null; emIso: string }>;
  criadoEmIso: string;
}

export interface ConfigDoOrcamento {
  markupPadrao: { id: string; percentual: number } | null;
  markupTecnico: { id: string; percentual: number } | null;
  markups: Array<{ id: string; percentual: number }>;
  diariaCentavos: number;
  horaCentavos: number;
  markupMoPct: number;
  empresaId: string;
  prazoRetornoDias: number;
  emailManutencao: string | null;
  emailCompras: string | null;
  textoEmail: string;
  proximoNumero: number;
  descontos: Array<{ id: string; percentual: number; ativo: boolean; ordem: number }>;
  pagamentos: Array<{ id: string; nome: string; minCentavos: number; maxCentavos: number | null; ativo: boolean; ordem: number }>;
  prazos: Array<{ id: string; texto: string; ativo: boolean; ordem: number }>;
}

export interface ProdutoBuscado {
  codigoProduto: string;
  codigo: string | null;
  descricao: string;
  unidade: string | null;
  custoUnitarioCentavos: number;
}

export interface MudancaDoOrcamento {
  titulo?: string;
  motivo?: string;
  maoModo?: 'diaria' | 'hora' | null;
  maoQtd?: number;
  descontoModo?: 'pct' | 'valor' | null;
  descontoPct?: number;
  descontoCentavos?: number;
  pagamento?: string | null;
  prazoEntrega?: string | null;
}

const B = '/api/v1/manutencao/orcamentos';
const enc = encodeURIComponent;

export const orcamentoApi = {
  fila: () => api.get<{ linhas: LinhaDaFila[]; contagem: Record<AbaDaFila, number> }>(B),
  detalhe: (id: string) => api.get<DetalheDoOrcamento>(`${B}/${enc(id)}`),
  config: () => api.get<ConfigDoOrcamento>(`${B}/config`),
  salvarConfig: (m: Partial<Omit<ConfigDoOrcamento, 'markupPadrao' | 'markupTecnico' | 'markups' | 'descontos' | 'pagamentos' | 'prazos' | 'proximoNumero'>> & { markupPadraoId?: string; markupTecnicoId?: string }) =>
    api.put<ConfigDoOrcamento>(`${B}/config`, m),
  acrescentarAoCatalogo: (d: { qual: 'desconto' | 'pagamento' | 'prazo'; percentual?: number; nome?: string; minCentavos?: number; maxCentavos?: number | null; texto?: string }) =>
    api.post<ConfigDoOrcamento>(`${B}/config/catalogo`, d),
  ligarNoCatalogo: (qual: 'desconto' | 'pagamento' | 'prazo', id: string, ativo: boolean) => api.patch<ConfigDoOrcamento>(`${B}/config/catalogo/${qual}/${enc(id)}`, { ativo }),
  produtos: (q: string) => api.get<{ items: ProdutoBuscado[]; total: number }>(`${B}/produtos?q=${enc(q)}`),
  criar: (d: { clienteId: string; titulo?: string; motivo?: string }) => api.post<DetalheDoOrcamento>(B, d),
  mudar: (id: string, m: MudancaDoOrcamento) => api.patch<DetalheDoOrcamento>(`${B}/${enc(id)}`, m),
  acrescentarItem: (id: string, d: { codigoProduto: string; quantidade: number }) => api.post<DetalheDoOrcamento>(`${B}/${enc(id)}/itens`, d),
  mudarItem: (id: string, itemId: string, d: { quantidade?: number; markupPct?: number }) => api.patch<DetalheDoOrcamento>(`${B}/${enc(id)}/itens/${enc(itemId)}`, d),
  removerItem: (id: string, itemId: string) => api.delete<DetalheDoOrcamento>(`${B}/${enc(id)}/itens/${enc(itemId)}`),
  enviar: (id: string, destinos?: string[]) =>
    api.post<{ detalhe: DetalheDoOrcamento; enviados: Array<{ email: string; status: string; comBotoes: boolean }> }>(`${B}/${enc(id)}/enviar`, destinos ? { destinos } : {}),
  resposta: (id: string, decisao: 'aprovado' | 'recusado', motivo?: string) => api.post<DetalheDoOrcamento>(`${B}/${enc(id)}/resposta`, { decisao, motivo: motivo ?? null }),
  desfazer: (id: string, motivo: string) => api.post<DetalheDoOrcamento>(`${B}/${enc(id)}/desfazer`, { motivo }),
  compras: (id: string, osVenda: string) => api.post<DetalheDoOrcamento>(`${B}/${enc(id)}/compras`, { osVenda }),
  urlDoPdf: (id: string) => `${B}/${enc(id)}/pdf`,
};
